import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthStatus, Organization } from '../types';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

interface AuthContextType {
  authStatus: AuthStatus;
  setAuthStatus: React.Dispatch<React.SetStateAction<AuthStatus>>;
  switchOrganization: (orgId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  installNewOrganization: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authStatus, setAuthStatus] = useState<AuthStatus>({
    authenticated: false,
    currentInstallation: null,
    organizations: [],
    currentOrganization: null,
    user: null,
  });

  useEffect(() => {
    loadUserSession();
  }, []);

  async function loadUserSession() {
    try {
      const response = await fetch('/api/auth/status', {
        credentials: 'same-origin',
      });

      if (response.ok) {
        const data = await response.json();
        setAuthStatus({
          authenticated: data.authenticated,
          currentInstallation: data.currentInstallation,
          organizations: data.organizations || [],
          currentOrganization: data.currentOrganization,
          user: data.user,
        });

        // Identify user in Google Analytics
        if (data.authenticated && data.user && window.gtag) {
          window.gtag('set', 'user_properties', {
            user_id: data.user.id.toString(),
            email: data.user.email,
            name: data.user.name,
            username: data.user.login,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load user session:', error);
    }
  }

  async function switchOrganization(orgId: string) {
    try {
      const response = await fetch('/api/organizations/switch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ organizationId: orgId }),
      });

      const data = await response.json();

      if (data.success) {
        setAuthStatus(prev => ({
          ...prev,
          currentOrganization: data.organization,
          currentInstallation: data.organization.installationId,
        }));
      }
    } catch (error) {
      console.error('Failed to switch organization:', error);
      throw error;
    }
  }

  async function refreshOrganizations() {
    try {
      const response = await fetch('/api/organizations/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      });

      if (response.ok) {
        const data = await response.json();
        setAuthStatus(prev => ({
          ...prev,
          organizations: data.organizations || [],
          currentOrganization: data.currentOrganization || prev.currentOrganization,
          currentInstallation: data.currentOrganization?.installationId || prev.currentInstallation,
        }));
      }
    } catch (error) {
      console.error('Failed to refresh organizations:', error);
      throw error;
    }
  }

  function installNewOrganization() {
    const appSlug = import.meta.env.VITE_PUBLIC_GITHUB_APP_SLUG || 'yukon-freeze-manager';
    const installUrl = `https://github.com/apps/${appSlug}/installations/new`;
    window.location.href = installUrl;
  }

  async function logout() {
    try {
      setAuthStatus(prev => ({ ...prev, authenticated: false }));
      window.location.replace('/auth/logout');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  return (
    <AuthContext.Provider
      value={{
        authStatus,
        setAuthStatus,
        switchOrganization,
        refreshOrganizations,
        installNewOrganization,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
